import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { classService } from '../services/classService';
import '../styles/AccountSettings.css';

const ClassSetupPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [currentClass, setCurrentClass] = useState(null);
  const [classMembers, setClassMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [pendingClassInvitations, setPendingClassInvitations] = useState([]);
  
  const [newClassName, setNewClassName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [lastInvitationLink, setLastInvitationLink] = useState('');

  useEffect(() => {
    loadClassData();
    loadInvitations();
  }, [currentUser]);

  useEffect(() => {
    if (currentClass && isAdmin) {
      loadPendingClassInvitations();
    }
  }, [currentClass, isAdmin]);

  const loadClassData = async () => {
    try {
      const classes = await classService.getUserClasses(currentUser.uid);
      if (classes.length > 0) {
        const classData = classes[0];
        setCurrentClass(classData);
        
        const adminStatus = await classService.isClassAdmin(currentUser.uid, classData.id);
        setIsAdmin(adminStatus);
        
        const members = await classService.getClassMembers(classData.id);
        setClassMembers(members);
      }
    } catch (err) {
      console.error('Error loading class data:', err);
    }
  };

  const loadInvitations = async () => {
    try {
      const invites = await classService.getClassInvitations(currentUser.email);
      setInvitations(invites);
    } catch (err) {
      console.error('Error loading invitations:', err);
    }
  };

  const loadPendingClassInvitations = async () => {
    try {
      const pendingInvites = await classService.getPendingInvitationsForClass(currentClass.id);
      setPendingClassInvitations(pendingInvites);
    } catch (err) {
      console.error('Error loading pending class invitations:', err);
    }
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    if (!newClassName.trim()) {
      setError('Please enter a class name');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const newClass = await classService.createClass(newClassName);
      setCurrentClass(newClass);
      setIsAdmin(true);
      setNewClassName('');
      setSuccess('Class created successfully!');
      loadClassData();
    } catch (err) {
      setError(err.message || 'Failed to create class');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await classService.sendClassInvitation(currentClass.id, inviteEmail);
      setInviteEmail('');
      setLastInvitationLink(result.invitationLink);
      setSuccess(`Invitation sent to ${inviteEmail}`);
      // Refresh pending invitations
      loadPendingClassInvitations();
    } catch (err) {
      setError(err.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async (invitationId) => {
    setLoading(true);
    setError('');

    try {
      const result = await classService.acceptClassInvitation(invitationId);
      
      let successMessage = 'Invitation accepted! You are now a class member.';
      
      if (result.eventsDistributed > 0) {
        successMessage += ` You have received ${result.eventsDistributed} existing class events.`;
      } else if (result.totalExistingEvents > 0) {
        successMessage += ' Note: Some existing class events may not have been added to avoid duplicates.';
      }
      
      setSuccess(successMessage);
      loadClassData();
      loadInvitations();
    } catch (err) {
      setError(err.message || 'Failed to accept invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineInvitation = async (invitationId) => {
    setLoading(true);
    setError('');

    try {
      await classService.declineClassInvitation(invitationId);
      loadInvitations();
    } catch (err) {
      setError(err.message || 'Failed to decline invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveClass = async () => {
    if (!window.confirm('Are you sure you want to leave this class?')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      await classService.leaveClass(currentClass.id);
      setCurrentClass(null);
      setClassMembers([]);
      setIsAdmin(false);
      setSuccess('You have left the class');
    } catch (err) {
      setError(err.message || 'Failed to leave class');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="account-settings-container">
      <div className="settings-header">
        <button 
          className="back-button" 
          onClick={() => navigate('/account-settings')}
        >
          ← Back to Settings
        </button>
        <h1>Class Setup</h1>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && (
        <div className="success-message">
          {success}
          {lastInvitationLink && (
            <div style={{ marginTop: '10px' }}>
              <strong>Invitation Link:</strong>
              <div style={{ 
                backgroundColor: 'white', 
                padding: '10px', 
                borderRadius: '4px', 
                marginTop: '5px',
                wordBreak: 'break-all',
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                border: '1px solid #c3e6cb'
              }}>
                {lastInvitationLink}
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(lastInvitationLink);
                  alert('Link copied to clipboard!');
                }}
                style={{
                  marginTop: '10px',
                  padding: '6px 12px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                Copy Link
              </button>
            </div>
          )}
        </div>
      )}

      {invitations.length > 0 && (
        <div className="settings-section">
          <h2>Pending Class Invitations</h2>
          {invitations.map(invitation => (
            <div key={invitation.id} className="invitation-card">
              <p>
                <strong>{invitation.inviterName}</strong> invited you to join <strong>{invitation.className}</strong>
              </p>
              <div className="button-group">
                <button 
                  className="primary-button"
                  onClick={() => handleAcceptInvitation(invitation.id)}
                  disabled={loading}
                >
                  Accept
                </button>
                <button 
                  className="secondary-button"
                  onClick={() => handleDeclineInvitation(invitation.id)}
                  disabled={loading}
                >
                  Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!currentClass ? (
        <div className="settings-section">
          <h2>Create a New Class</h2>
          <form onSubmit={handleCreateClass}>
            <div className="form-group">
              <label htmlFor="className">Class Name</label>
              <input
                id="className"
                type="text"
                value={newClassName}
                onChange={(e) => setNewClassName(e.target.value)}
                placeholder="Enter class name"
                disabled={loading}
              />
            </div>
            <button 
              type="submit" 
              className="primary-button"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Class'}
            </button>
          </form>
        </div>
      ) : (
        <>
          <div className="settings-section">
            <h2>Current Class</h2>
            <div className="info-box">
              <p><strong>Class Name:</strong> {currentClass.name}</p>
              <p><strong>Your Role:</strong> {isAdmin ? 'Admin' : 'Member'}</p>
              <p><strong>Total Members:</strong> {classMembers.length}</p>
              <p><strong>Class ID:</strong> {currentClass.id}</p>
            </div>
            {!isAdmin && (
              <button 
                className="secondary-button"
                onClick={handleLeaveClass}
                disabled={loading}
              >
                Leave Class
              </button>
            )}
          </div>

          {isAdmin && (
            <div className="settings-section">
              <h2>Invite New Members</h2>
              <form onSubmit={handleSendInvite}>
                <div className="form-group">
                  <label htmlFor="inviteEmail">Email Address</label>
                  <input
                    id="inviteEmail"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="Enter email to invite"
                    disabled={loading}
                  />
                </div>
                <button 
                  type="submit" 
                  className="primary-button"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Invitation'}
                </button>
              </form>
            </div>
          )}

          <div className="settings-section">
            <h2>Class Members</h2>
            <div className="members-list">
              {classMembers.map(member => (
                <div key={member.id} className="member-item">
                  <div>
                    <p className="member-name">
                      {member.displayName || member.email}
                      {member.isAdmin && <span className="admin-badge"> (Admin)</span>}
                    </p>
                    <p className="member-email">{member.email}</p>
                  </div>
                </div>
              ))}
              
              {pendingClassInvitations.length > 0 && (
                <>
                  <div style={{
                    margin: '20px 0 15px 0',
                    padding: '10px 0',
                    borderTop: '1px solid #e9ecef'
                  }}>
                    <p style={{
                      fontSize: '0.9rem',
                      color: '#666',
                      fontWeight: '600',
                      margin: 0
                    }}>Pending Invitations</p>
                  </div>
                  
                  {pendingClassInvitations.map(invitation => (
                    <div key={invitation.id} className="member-item" style={{
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffeeba'
                    }}>
                      <div>
                        <p className="member-name">
                          {invitation.recipientEmail}
                          <span style={{
                            color: '#856404',
                            fontSize: '0.85rem',
                            fontWeight: 'normal',
                            marginLeft: '8px'
                          }}>(Pending)</span>
                        </p>
                        <p className="member-email" style={{ color: '#856404', fontSize: '0.85rem' }}>
                          Invited on {invitation.createdAt?.toDate ? 
                            new Date(invitation.createdAt.toDate()).toLocaleDateString() : 
                            'Recently'}
                        </p>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ClassSetupPage;